import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "367",
  "slug": "what-is-a-watershed",
  "title": "What Is a Watershed? How to See River Basins From Space",
  "excerpt": "Every drop of rain that falls eventually flows somewhere. A watershed is the area of land that funnels water into one river, and the boundaries between them shape borders, cities, and conflicts.",
  "date": "2026-06-07",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1719457842743-0e00f72c5d5f?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxyaXZlciUyMHZhbGxleSUyMGFlcmlhbHxlbnwxfDB8fHwxNzgwODQ1NDI0fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "A watershed, or drainage basin, is all the land where rainfall collects and drains into a single river system. Ridgelines divide one basin from the next, and the whole branching pattern is visible from above.",
  "content": [
    {
      "type": "paragraph",
      "text": "Imagine standing on a hilltop in the rain. The drops that land on one side of you trickle down into one valley and join one stream; the drops on the other side flow into a different valley entirely. That hilltop is a boundary, and the entire area of land that funnels its water into a single river or lake is called a watershed, also known as a drainage basin or catchment. Watersheds are one of the most fundamental ways geographers divide up the surface of the Earth, and once you can see them, the logic of rivers snaps into focus."
    },
    {
      "type": "heading",
      "text": "What a Watershed Actually Is"
    },
    {
      "type": "paragraph",
      "text": "A watershed includes every bit of land where falling rain and melting snow drain toward the same outlet. Small streams gather into larger ones, which gather into a main river, which eventually reaches the sea, a lake, or an inland basin. A big watershed is made up of countless smaller sub-watersheds nested inside it, like branches feeding into a trunk. The Amazon basin, for example, collects water from an area roughly the size of Australia, draining a huge slice of South America into one colossal river."
    },
    {
      "type": "heading",
      "text": "Divides: The Ridges Between Basins"
    },
    {
      "type": "paragraph",
      "text": "The boundary between two watersheds is called a divide, and it almost always follows high ground: a ridge, a range of hills, a line of mountains. Rain that falls on one side of the divide ends up in a completely different river system from rain that falls a few metres away on the other side. Because divides follow natural high ground, they have been used as borders between countries and provinces for centuries; the line is obvious, permanent, and hard to argue with."
    },
    {
      "type": "heading",
      "text": "The World's Great River Basins"
    },
    {
      "type": "paragraph",
      "text": "A handful of enormous basins drain much of the inhabited world. The Amazon is the largest by far, followed by the Congo in central Africa and the Mississippi-Missouri system in North America. The Nile drains an astonishing length of northeast Africa; the Ganges, Yangtze, and Mekong basins between them sustain a huge share of humanity. Each basin is a self-contained water world, and the river at its centre reflects everything that happens on the land around it."
    },
    {
      "type": "heading",
      "text": "Why Watersheds Matter to People"
    },
    {
      "type": "paragraph",
      "text": "Watersheds are the natural unit of water management. Everyone living in a basin shares the same water, which means pollution dumped upstream, forests cleared upstream, or a dam built upstream affects everyone downstream. Because rivers cross borders, watersheds are also a frequent source of international negotiation and occasional conflict, as countries that share a basin work out how to divide a finite flow. Thinking in watersheds, rather than in political boundaries, is often the only way water problems make sense."
    },
    {
      "type": "paragraph",
      "text": "This is why the watershed has become the basic unit of environmental planning. Rather than managing a river town by town or country by country, agencies increasingly manage the whole basin as one connected system, because that is how water actually behaves. Restore a forest in the headwaters and the benefits, cleaner water, steadier flow, and less flooding, ripple all the way down to the coast."
    },
    {
      "type": "heading",
      "text": "Continental Divides"
    },
    {
      "type": "paragraph",
      "text": "On the largest scale, entire continents are split by great divides that send water toward different oceans. In North America, the Continental Divide runs down the spine of the Rocky Mountains: rain on the eastern slope eventually reaches the Atlantic, while rain on the western slope flows to the Pacific. Similar grand divides cross every major landmass. They are the ultimate watershed boundaries, deciding which ocean a raindrop will join based on the side of a mountain it happened to land on."
    },
    {
      "type": "heading",
      "text": "Reading Drainage From Space"
    },
    {
      "type": "paragraph",
      "text": "From satellite imagery, drainage patterns are everywhere once you start looking. Rivers and their tributaries branch like the veins of a leaf, and the texture of that branching reveals the land beneath: tightly packed feathery channels suggest soft, easily eroded ground, while rectangular, angular drainage hints at underlying fractures in hard rock. Trace the streams uphill to the ridgelines and you are effectively mapping the watershed by eye, seeing the invisible boundaries that govern where every drop of water goes."
    }
  ]
};
